import * as React from "react";
import { cn } from "@/lib/utils";
import { splitClasses } from "@/components/ui/date-input";

/*
 * Every number field: the browser's own number box, plus the checks it lacks. Letters, e / E (1e5) and + are never let in; a minus only when the
 * field allows negatives (no min, or min below 0); decimals no finer than the field's step (step="1" = whole numbers, step="0.01" = 2 places;
 * no step = any). Typing, phone keyboards (beforeinput), paste and drag-and-drop all go through the same check, and a refused key shows a short
 * message under the field for a moment.
 */
export function decimalsOf(step) {
  if (step === undefined || step === null || step === "" || String(step).toLowerCase() === "any") return null;
  const s = String(step);
  if (!/^\d*\.?\d+$/.test(s)) return null;
  return s.includes(".") ? s.split(".")[1].length : 0;
}

export function allowsNegative(min) {
  return min === undefined || min === null || min === "" || Number(min) < 0;
}

/** Why `text` cannot be a value of this field, or null when it can. A partly typed number ("", "-", "12.") is fine. */
export function problem(text, { min, step } = {}) {
  if (text === "") return null;
  if (/[eE+]/.test(text)) return "Numbers only";
  if (!allowsNegative(min) && text.includes("-")) return "No minus here";
  if (!/^-?\d*\.?\d*$/.test(text)) return "Numbers only";
  const places = decimalsOf(step);
  const dec = text.includes(".") ? text.split(".")[1].length : 0;
  if (places !== null && dec > places) return places === 0 ? "Whole numbers only" : `Up to ${places} decimal place${places === 1 ? "" : "s"}`;
  return null;
}

const NumberInput = React.forwardRef(function NumberInput({ className, min, step, onKeyDown, onPaste, onDrop, onBeforeInput, onChange, ...props }, ref) {
  const [note, setNote] = React.useState("");
  const timer = React.useRef(null);
  React.useEffect(() => () => clearTimeout(timer.current), []);
  const say = (msg) => { setNote(msg); clearTimeout(timer.current); timer.current = setTimeout(() => setNote(""), 2000); };
  const { outer, inner } = splitClasses(className);
  const own = React.useRef(null);
  const setRef = (el) => { own.current = el; if (typeof ref === "function") ref(el); else if (ref) ref.current = el; };
  const rules = React.useRef({ min, step });
  rules.current = { min, step };
  // phone keyboards send no key codes; the browser's own beforeinput event still says what is about to be typed
  React.useEffect(() => {
    const el = own.current;
    if (!el) return undefined;
    const before = (ev) => {
      if (typeof ev.data !== "string" || !ev.data.length) return;
      const v = el.value ?? "";
      const msg = problem(v + ev.data, rules.current) || (/[^\d.-]/.test(ev.data) ? "Numbers only" : null);
      if (msg) { ev.preventDefault(); say(msg); }
    };
    el.addEventListener("beforeinput", before);
    return () => el.removeEventListener("beforeinput", before);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // what the field would hold if `insert` replaced the current selection
  const would = (el, insert) => {
    const v = el.value ?? "";
    let s = el.selectionStart, e = el.selectionEnd;
    if (s === null || s === undefined) { s = v.length; e = v.length; }      // a number box often hides its caret: treat it as the end
    return v.slice(0, s) + insert + v.slice(e);
  };
  const keyDown = (ev) => {
    const k = ev.key;
    if (k && k.length === 1 && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
      if (/[eE+]/.test(k) || (!/[\d.-]/.test(k))) { ev.preventDefault(); say("Numbers only"); }
      else if (k === "-" && !allowsNegative(min)) { ev.preventDefault(); say("No minus here"); }
      else if (k === "." && decimalsOf(step) === 0) { ev.preventDefault(); say("Whole numbers only"); }
      else if (/\d/.test(k)) {
        const places = decimalsOf(step), v = ev.currentTarget.value ?? "";
        if (places !== null && places > 0 && v.includes(".") && v.split(".")[1].length >= places && (ev.currentTarget.selectionStart ?? v.length) > v.indexOf(".")
            && (ev.currentTarget.selectionStart ?? v.length) === (ev.currentTarget.selectionEnd ?? v.length)) {
          ev.preventDefault(); say(`Up to ${places} decimal place${places === 1 ? "" : "s"}`);
        }
      }
    }
    onKeyDown?.(ev);
  };
  const checkInsert = (ev, text) => {
    const msg = problem(would(ev.currentTarget, text), { min, step });
    if (msg) { ev.preventDefault(); say(msg); return false; }
    return true;
  };
  const paste = (ev) => { checkInsert(ev, (ev.clipboardData?.getData("text") || "").trim()); onPaste?.(ev); };
  const drop = (ev) => { const t = (ev.dataTransfer?.getData("text") || "").trim(); if (problem(t, { min, step })) { ev.preventDefault(); say(problem(t, { min, step })); } onDrop?.(ev); };
  const change = (ev) => {                                                 // last line of defence (autofill, scripts): never pass a bad value on
    const v = ev.target.value ?? "";
    const msg = problem(String(v), { min, step });
    if (msg) { say(msg); return; }
    onChange?.(ev);
  };

  return (
    <div className={cn("relative", /(^|\s)(?:[a-z]+:)?(?:w-|flex-1|grow)/.test(outer) ? "" : "w-full", outer)}>
      <input ref={setRef} type="number" min={min} step={step} onKeyDown={keyDown} onBeforeInput={onBeforeInput} onPaste={paste} onDrop={drop} onChange={change}
        className={cn("flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          note && "border-rose-400", inner)}
        aria-invalid={note ? true : undefined} {...props} />
      {note && <span role="status" className="pointer-events-none absolute left-1 top-full z-10 mt-0.5 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] text-rose-700 shadow-sm dark:bg-rose-950 dark:text-rose-300" data-testid={props["data-testid"] ? `${props["data-testid"]}-note` : "number-note"}>{note}</span>}
    </div>
  );
});

export { NumberInput };
